import { com, kotlin } from '@eidu/web'
import Link from '../../../../domain/entity/Link'
import FormContentIdAndHash from '../../../../domain/form/FormContentIdAndHash'
import { ensurePageKeys } from '../../../../io/useFormContent'
import { newEmptyFormContent } from './formContentYaml'
import KtList = kotlin.collections.KtList
import Page = com.eidu.sharedlib.form.definition.Page
import EntityId = com.eidu.sharedlib.entity.EntityId
import FormContent = com.eidu.sharedlib.form.FormContent
import FormValidator = com.eidu.sharedlib.form.validation.FormValidator
import toException = com.eidu.sharedlib.form.validation.toException
import encodeFormContentToYaml = com.eidu.sharedlib.form.encodeFormContentToYaml
import decodeFormContentFromYaml = com.eidu.sharedlib.form.decodeFormContentFromYaml
import formContentIdKey = com.eidu.sharedlib.entity.formContentIdKey
import formContentHashKey = com.eidu.sharedlib.entity.formContentHashKey
import NonEmptyList = com.quickbirdstudios.nonEmptyCollection.list.NonEmptyList

export type FormService = {
  postForm: (args: { formId: string; initialContent: FormContent }) => Promise<FormContentIdAndHash>
  postContent: (args: { formId: string; content: FormContent }) => Promise<FormContentIdAndHash>
  getContent: (args: { formId: string; formContentId: string }) => Promise<KtList<Page>>
}

// Identifies the form-service content an entity is already linked to (its formContentId link).
export type ExistingFormContentRef = {
  formId: string
  formContentId: string
}

export type FormSave = {
  seedPayload: () => Promise<string>
  validatePayload: (payload: string) => void
  buildContentLinks: (args: { entityId: EntityId; payload: string }) => Promise<Link[]>
}

type CreateFormSaveArgs = {
  existingContent: ExistingFormContentRef | undefined
  formService: FormService
}

/**
 * The Form save module: owns the save choreography for the Form asset kind — seeding the editor from legacy
 * form-service content, the pre-submit validation gate (see ADR-0001), and the dual-write of form-service content.
 */
const createFormSave = ({ existingContent, formService }: CreateFormSaveArgs): FormSave => ({
  // Consulted only when the entity has no asset.resource link yet: read legacy form-service content, else start empty.
  seedPayload: async () =>
    encodeFormContentToYaml(
      existingContent ? ensurePageKeys(await formService.getContent(existingContent)) : newEmptyFormContent()
    ),

  validatePayload: (payload: string) => {
    const pages = decodeFormContentFromYaml(payload)
    const errors = FormValidator.validatePages(KtList.fromJsArray(pages.asJsReadonlyArrayView()))
    if (errors.asJsReadonlyArrayView().length > 0) throw toException(errors)
  },

  buildContentLinks: async ({ entityId, payload }): Promise<Link[]> => {
    const pages = decodeFormContentFromYaml(payload)
    const content = new FormContent(crypto.randomUUID(), NonEmptyList.fromArray([...pages.asJsReadonlyArrayView()]))
    // Routed by the formContentId link, not by whether the entity exists: an entity whose original postForm failed
    // carries no link, so re-saving retries the form-service create instead of posting content to a form that was
    // never created (which would fail forever).
    const { contentId, contentSha256 } = existingContent
      ? await formService.postContent({ formId: existingContent.formId, content })
      : await formService.postForm({ formId: entityId.toString(), initialContent: content })
    return [
      { key: formContentIdKey, value: contentId.toString() },
      { key: formContentHashKey, value: contentSha256 },
    ]
  },
})

export default createFormSave
